import { CdkDrag, CdkDragDrop, CdkDragPlaceholder, CdkDropList } from '@angular/cdk/drag-drop';
import { AfterViewInit, ChangeDetectionStrategy, Component, effect, model, signal, untracked, ViewChild, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FULL_TEXT_SEARCH_SCORE_KEY, FUZZY_SIMILARITY_SORT_KEY } from '@phoenix/common';
import { simpleDeepClone } from '@phx-erp/shared';
import { SharedModule, TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TreeSelect, TreeSelectModule } from 'primeng/treeselect';
import { S_SORT_CHOOSER_FULL_LOAD_BASE } from '../../../@core/constants/settings-keys';
import { ColumnService } from '../../../@core/services/column.service';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { SettingsService } from '../../../@core/services/settings.service';
import { array_move } from '../../../@core/utils/helper';
import { ServerConfigService } from '../../../data/server-config';
import { ColumnDefinition, CustomFieldType, EntitySearchSettings, NullSortOrder, SortOrder, SortParameter } from '../../../generated/generated-types';
import { PhxCheckboxComponent } from '../phx-input-checkbox/phx-input-checkbox.component';

const non_object_types = ['string', 'number', 'boolean', 'datetime', 'date', 'float', 'json', 'int', 'numeric'];

/** Must match server `COLUMN_CHOOSER_EDITOR_MAX_DEPTH` in column-definition.resolver.ts */
const SORT_CHOOSER_EDITOR_MAX_DEPTH = 5;

const parseBooleanSetting = (value: unknown): boolean => {
  if (typeof value === 'boolean')
    return value;
  if (value === 'true' || value === 1)
    return true;
  if (value === 'false' || value === 0)
    return false;
  return false;
};

//most comments are related to full text search score, which is currently not implemented
//remove comments with care
@Component({
  selector: 'app-sort-chooser',
  imports: [
    SharedModule,
    TreeSelectModule,
    ReactiveFormsModule,
    FormsModule,
    TagModule,
    ButtonModule,
    PhxCheckboxComponent,
    TranslatePipe,
    TooltipModule,
    CdkDropList,
    CdkDrag,
    CdkDragPlaceholder,
    ProgressSpinnerModule,
    SkeletonModule,
  ],
  templateUrl: './sort-chooser.component.html',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SortChooserComponent extends FieldType<FieldTypeConfig> implements AfterViewInit, ControlValueAccessor {
  @ViewChild(TreeSelect) private treeSelect?: TreeSelect;

  entityName = model<string>();

  nodes = model<TreeNode<SortParameter>[]>([]);
  selectedNodes = model<TreeNode<SortParameter>[]>([]);

  value = model<SortParameter[]>([]);

  isInitialized = false;
  showExtendedOptions = model<boolean>(false);

  disabled = model<boolean>(false);
  loading = model<boolean>(false);

  /** Mutable column tree for lazy expand (mirrors column chooser `typeCols`). */
  private sortColumnDefsRoot: ColumnDefinition[] = [];
  lazyExpandLoadingKey = signal<string | null>(null);

  showFuzzySimilarityNode = model<boolean>(false);
  fuzzySimilarityNode: TreeNode<SortParameter> = {
    key: FUZZY_SIMILARITY_SORT_KEY,
    label: this.i18nService.translate(_('entity-search-settings.fuzzy-similarity')),
    data: {
      field: FUZZY_SIMILARITY_SORT_KEY,
      order: SortOrder.DESC,
      nulls: NullSortOrder.LAST,
    },
  };

  showFullTextSearchScoreNode = model<boolean>(false);
  fullTextSearchScoreNode: TreeNode<SortParameter> = {
    key: FULL_TEXT_SEARCH_SCORE_KEY,
    label: this.i18nService.translate(_('entity-search-settings.full-text-search-score')),
    data: {
      field: FULL_TEXT_SEARCH_SCORE_KEY,
      order: SortOrder.DESC,
      nulls: NullSortOrder.LAST,
    },
  };

  //for use in html
  FUZZY_SIMILARITY_SORT_KEY = FUZZY_SIMILARITY_SORT_KEY;
  FULL_TEXT_SEARCH_SCORE_KEY = FULL_TEXT_SEARCH_SCORE_KEY;
  Math = Math;

  constructor(
    private columnService: ColumnService,
    private i18nService: I18nService,
    protected serverConfigService: ServerConfigService,
    private settingsService: SettingsService,
  ) {
    super();
    effect(() => {
      if (this.entityName()) {
        void this.loadData();
      }
    });
    effect(() => {
      let nodes = this.nodes();
      let showFuzzySimilarityNode = this.showFuzzySimilarityNode();

      if (!nodes || nodes.length === 0)
        return;

      untracked(() => this.addOrRemoveNode(FUZZY_SIMILARITY_SORT_KEY, this.fuzzySimilarityNode, showFuzzySimilarityNode));
    });

    // effect(() => {
    //   let showFullTextSearchScoreNode = this.showFullTextSearchScoreNode();
    //   let nodes = this.nodes();

    //   if (!nodes || nodes.length === 0)
    //     return;

    //   untracked(() => this.addOrRemoveNode(FULL_TEXT_SEARCH_SCORE_KEY, this.fullTextSearchScoreNode, showFullTextSearchScoreNode && !this.showFuzzySimilarityNode()));
    // });
  }

  /** When false (default), sort treeselect uses depth-2 + lazy expand; when true, full nested tree via getAllColumnsOfType. */
  isSortChooserFullLoad(): boolean {
    const key = this.entityName();
    if (!key)
      return false;
    return parseBooleanSetting(this.settingsService.getSetting(S_SORT_CHOOSER_FULL_LOAD_BASE + key, false));
  }

  setSortChooserFullLoad(full: boolean): void {
    const key = this.entityName();
    if (!key)
      return;
    this.settingsService.setSetting(S_SORT_CHOOSER_FULL_LOAD_BASE + key, full);
    void this.loadData();
  }

  private sortChooserLazyExpansion(): boolean {
    return !this.isSortChooserFullLoad();
  }

  private isFieldPathBelowChooserMaxDepth(field: string | undefined): boolean {
    const n = field?.split('.').filter(Boolean).length ?? 0;
    return n < SORT_CHOOSER_EDITOR_MAX_DEPTH;
  }

  private isComplexColumnForChooser(col: ColumnDefinition): boolean {
    const dt = col.dataType?.toLowerCase() ?? '';
    if (['string', 'number', 'boolean', 'datetime', 'date', 'float', 'json', 'int', 'numeric'].includes(dt))
      return false;
    if (col.isEnum)
      return false;
    if (col.dataType && Object.keys(CustomFieldType).includes(col.dataType))
      return false;
    return true;
  }

  private shouldLazySortPlaceholder(col: ColumnDefinition, dottedKey: string): boolean {
    if ((col.subColumns?.length ?? 0) > 0)
      return false;
    if (!this.isFieldPathBelowChooserMaxDepth(dottedKey))
      return false;
    if (col.hasExpandableChildren === false)
      return false;
    return col.hasExpandableChildren === true
      || (col.hasExpandableChildren !== false && this.isComplexColumnForChooser(col));
  }

  /**
   * Filters column defs for the sort treeselect (same idea as column chooser `filterColumns`).
   * @param lazyPlaceholders keep complex object columns without subColumns (depth-limited mode).
   */
  private filterSortColumns(cols: ColumnDefinition[], lazyPlaceholders = false): ColumnDefinition[] {
    const result: ColumnDefinition[] = [];
    for (const col of cols) {
      if (col.subColumns?.length > 0) {
        const subColumns = this.filterSortColumns(col.subColumns, lazyPlaceholders);
        if (subColumns?.length > 0)
          result.push({ ...col, subColumns });
      }
      else if (non_object_types.includes(col.dataType.toLowerCase()) || col.isEnum || (col.dataType && Object.keys(CustomFieldType).includes(col.dataType)))
        result.push(col);
      else if (lazyPlaceholders && this.isComplexColumnForChooser(col))
        result.push(col);
    }
    return result;
  }

  /** Walk defs by `prop` segments (same as column chooser `getColumnDefByFieldName`). */
  private getColumnDefByFieldName(colDefs: ColumnDefinition[], fieldName: string | string[]): ColumnDefinition | undefined {
    const parts = Array.isArray(fieldName) ? [...fieldName] : fieldName.split('.');
    const currentPart = parts.shift();
    for (const colDef of colDefs) {
      if (colDef.prop == currentPart) {
        if (parts.length === 0)
          return colDef;
        if (colDef.subColumns?.length > 0)
          return this.getColumnDefByFieldName(colDef.subColumns, parts);
      }
    }
    return undefined;
  }

  private findColDefByField(cols: ColumnDefinition[], field: string): ColumnDefinition | null {
    for (const c of cols) {
      if (c.field === field)
        return c;
      if (c.subColumns?.length) {
        const found = this.findColDefByField(c.subColumns, field);
        if (found)
          return found;
      }
    }
    return null;
  }

  addOrRemoveNode(key: string, node: TreeNode<SortParameter>, add: boolean) {
    if (add) {
      if (!this.nodes().some(x => x.key === key))
        this.nodes.set([node, ...this.nodes()]);
      if (!this.selectedNodes().some(x => x.key === key)) {
        this.selectedNodes.set([node, ...this.selectedNodes()]);
        this.updateValue();
      }
    } else if (!add) {
      if (this.nodes().some(x => x.key === key))
        this.nodes.set(this.nodes().filter(x => x.key !== key));
      if (this.selectedNodes().some(x => x.key === key)) {
        this.selectedNodes.set(this.selectedNodes().filter(x => x.key !== key));
        this.updateValue();
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.props?.entityName)
      this.entityName.set(this.props.entityName);

    if (this.formControl?.value)
      this.parseValue(this.formControl.value);
    this.formControl?.valueChanges.subscribe(value => {
      if (this.value() !== value) {
        if (this.isInitialized) {
          this.parseValue(value);
        }
        this.value.set(value);
      }
    });

    // if (this.props?.showFullTextSearchScoreNode || this.form?.value?.searchableFields?.length > 0)
    //   this.showFullTextSearchScoreNode.set(this.props.showFullTextSearchScoreNode);
    if (this.props?.showFuzzySimilarityNode || this.form?.value?.fuzzySearchEnabled)
      this.showFuzzySimilarityNode.set(this.props.showFuzzySimilarityNode);

    this.form?.valueChanges.subscribe({
      next: (value: EntitySearchSettings) => {
        if (!value)
          return;

        //show/hide fuzzy similarity node
        if (value.fuzzySearchEnabled && !this.showFuzzySimilarityNode())
          this.showFuzzySimilarityNode.set(true);
        else if (!value.fuzzySearchEnabled && this.showFuzzySimilarityNode())
          this.showFuzzySimilarityNode.set(false);

        //show/hide full text search score node
        // if (!value.fuzzySearchEnabled && value.searchableFields?.length > 0 && !this.showFullTextSearchScoreNode())
        //   this.showFullTextSearchScoreNode.set(true);
        // else if ((value.fuzzySearchEnabled || !value.searchableFields?.length) && this.showFullTextSearchScoreNode())
        //   this.showFullTextSearchScoreNode.set(false);
      }
    });
  }

  private parseValue(value: SortParameter[]): TreeNode<SortParameter>[] {
    if (!value)
      return [];
    let selectedNodes: TreeNode<SortParameter>[] = [];
    for (let item of value) {
      let node = this.findNode(item.field, this.nodes());
      if (node) {
        node.data = item;
        selectedNodes.push(node);
      }
    }
    this.selectedNodes.set(selectedNodes);
    return selectedNodes;
  }

  findNode(key: string, nodes: TreeNode<SortParameter>[]): TreeNode<SortParameter> | undefined {
    for (const node of nodes) {
      if (node.key === key) {
        return node;
      }
      if (key.startsWith(node.key + '.') && node.children?.length) {
        const found = this.findNode(key, node.children);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }

  async loadData() {
    const entity = this.entityName();
    if (!entity) {
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.translateCache.clear();

    try {
      let columns: ColumnDefinition[];

      if (this.isSortChooserFullLoad()) {
        columns = await this.columnService.getAllColumnsOfType(entity, true);
      } else {
        let depthCols = simpleDeepClone(await this.columnService.getColumnChooserColumnsByDepth(entity, 2));
        depthCols = this.filterSortColumns(depthCols, true);

        const sortParams = (this.formControl?.value as SortParameter[]) ?? this.value() ?? [];
        const selectedFields = sortParams
          .map(s => s.field)
          .filter(f => f && f !== FUZZY_SIMILARITY_SORT_KEY && f !== FULL_TEXT_SEARCH_SCORE_KEY);

        const missingSelectedPaths = selectedFields.filter(f => !this.getColumnDefByFieldName(depthCols, f));
        if (missingSelectedPaths.length > 0) {
          const extra = await this.columnService.getColumnChooserTreeForFieldPaths(entity, missingSelectedPaths);
          columns = this.columnService.mergeColumnDefinitions(
            depthCols as any,
            simpleDeepClone(extra) as any,
          ) as ColumnDefinition[];
        } else {
          columns = depthCols;
        }
      }

      this.sortColumnDefsRoot = simpleDeepClone(columns);

      const lazyExpansion = this.sortChooserLazyExpansion();
      let nodes = this.columnDefsToTreeNode(columns, '', lazyExpansion);
      // if (this.showFullTextSearchScoreNode() && !nodes.some(x => x.key === FULL_TEXT_SEARCH_SCORE_KEY))
      //   nodes.unshift(this.fullTextSearchScoreNode);
      if (this.showFuzzySimilarityNode() && !nodes.some(x => x.key === FUZZY_SIMILARITY_SORT_KEY))
        nodes.unshift(this.fuzzySimilarityNode);
      this.nodes.set(nodes);

      this.parseValue((this.formControl?.value as SortParameter[]) ?? this.value() ?? []);

      this.isInitialized = true;
    } catch (e: any) {
      console.error(e);
    } finally {
      this.loading.set(false);
    }
  }

  async onNodeExpand($event: { node: TreeNode<SortParameter> }) {
    if (!this.sortChooserLazyExpansion())
      return;

    const node = $event.node;
    const field = node.data.field;

    if ((node.children?.length ?? 0) > 0)
      return;

    const colDef = this.findColDefByField(this.sortColumnDefsRoot, field);
    if (!colDef?.dataType)
      return;
    if (colDef.hasExpandableChildren === false)
      return;
    if (!this.isFieldPathBelowChooserMaxDepth(field))
      return;
    if (colDef.hasExpandableChildren !== true && !this.isComplexColumnForChooser(colDef))
      return;

    this.lazyExpandLoadingKey.set(node.key as string);
    this.treeSelect?.cd.markForCheck();
    try {
      const childDefs = await this.columnService.getColumnChooserChildrenForPath(
        field,
        colDef.dataType,
      );
      const filtered = this.filterSortColumns(childDefs, true);

      const parentInTree = this.findColDefByField(this.sortColumnDefsRoot, field);
      if (parentInTree)
        parentInTree.subColumns = filtered;

      const lazyExpansion = this.sortChooserLazyExpansion();
      node.children = this.columnDefsToTreeNode(filtered, field, lazyExpansion);
      node.expanded = true;
      // Do NOT reassign `nodes` (would fire TreeSelect.options setter → resetExpandedNodes).
      // Do NOT reassign `selectedNodes` here (would fire writeValue → resetExpandedNodes).
      this.treeSelect?.cd.markForCheck();
    } catch (error: any) {
      console.error(`Failed to load sort chooser column children for ${field}: ${error?.message ?? error}`, error);
    } finally {
      this.lazyExpandLoadingKey.set(null);
      this.treeSelect?.cd.markForCheck();
    }
  }

  translateCache = new Map<string, string>();
  arrayToString(array: string[] | string): string {
    if (Array.isArray(array))
      return array.join('.');
    return array;
  }
  translate(key: string | string[], params?: any): string {
    key = this.arrayToString(key);
    if (this.translateCache.has(key))
      return this.translateCache.get(key);
    let result = null;
    let translation = this.i18nService.translate(key, params);
    if (translation === key) {
      result = key.split('.').at(-1);
    }
    if (!result) {
      if (typeof translation === 'object' && (translation as any)?._)
        result = (translation as any)._;
      else if (typeof translation === 'string')
        result = translation;
    }
    if (result)
      this.translateCache.set(key, result);
    return result;
  }

  columnDefsToTreeNode(columns: ColumnDefinition[], parentKey: string = '', lazyExpansion = false): TreeNode<SortParameter>[] {
    const rowKey = (x: ColumnDefinition) => parentKey ? parentKey + '.' + (x.prop ?? x.field) : (x.prop ?? x.field);

    const hasSubColumnsForSort = (x: ColumnDefinition, key: string) => {
      const loadedBranch = (x.subColumns?.length ?? 0) > 0 && !non_object_types.includes(x.dataType.toLowerCase()) && !x.isArray;
      if (loadedBranch)
        return true;
      if (lazyExpansion && this.shouldLazySortPlaceholder(x, key))
        return true;
      return false;
    };

    let columnsWithLabels = columns
      .filter(x => {
        const scalar = non_object_types.includes(x.dataType.toLowerCase()) && !x.isEnum && !x.isArray && !this.isAnIdField(x);
        const key = rowKey(x);
        if (scalar)
          return true;
        if ((x.subColumns?.length ?? 0) > 0)
          return true;
        if (lazyExpansion && this.shouldLazySortPlaceholder(x, key))
          return true;
        return false;
      })
      .map(x => ({ ...x, label: this.i18nService.translate([this.entityName(), parentKey, (x.prop ?? x.field)].filter(x => (x?.length ?? 0) > 0).join('.')) }) as ColumnDefinition & { label: string });

    columnsWithLabels = columnsWithLabels.sort((a, b) => {
      const keyA = rowKey(a);
      const keyB = rowKey(b);
      //custom fields should always be at the top
      if ((a.prop ?? a.field) === 'customFields') return -1;
      if ((b.prop ?? b.field) === 'customFields') return 1;
      //fields with sub columns should always be at the bottom
      if (hasSubColumnsForSort(a, keyA) && !hasSubColumnsForSort(b, keyB)) return 1;
      if (!hasSubColumnsForSort(a, keyA) && hasSubColumnsForSort(b, keyB)) return -1;
      //sort by label
      return a.label.localeCompare(b.label);
    });

    return columnsWithLabels.map(x => {
      const key = rowKey(x);
      let children: TreeNode<SortParameter>[] | undefined;
      let leaf: boolean;

      if ((x.subColumns?.length ?? 0) > 0) {
        children = this.columnDefsToTreeNode(x.subColumns, key, lazyExpansion);
        leaf = false;
      } else if (!lazyExpansion) {
        children = undefined;
        leaf = true;
      } else if (x.hasExpandableChildren === false) {
        children = undefined;
        leaf = true;
      } else if (this.shouldLazySortPlaceholder(x, key)) {
        children = [];
        leaf = false;
      } else {
        children = undefined;
        leaf = true;
      }

      return {
        key,
        label: x.label,
        data: this.value()?.find(y => y.field === key) ?? {
          field: key,
          order: SortOrder.ASC,
          nulls: NullSortOrder.LAST,
        },
        selectable: non_object_types.includes(x.dataType.toLowerCase()) && !x.isArray && !x.isEnum,
        children,
        leaf,
      };
    });
  }

  isAnIdField(column: { prop?: string, field?: string }): boolean {
    let value = (column.prop ?? column.field);
    return value == "id" || value.endsWith("Id");
  }

  toggleOrder(key: string) {
    let node = this.selectedNodes().find(x => x.key === key);
    node.data.order = node.data.order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
    this.updateValue();
  }

  toggleNulls(key: string) {
    let node = this.selectedNodes().find(x => x.key === key);
    node.data.nulls = node.data.nulls === NullSortOrder.FIRST ? NullSortOrder.LAST : NullSortOrder.FIRST;
    this.updateValue();
  }

  remove(key: string) {
    this.selectedNodes.set(this.selectedNodes().filter(x => x.key !== key));
    this.updateValue();
  }

  onDrop(event: CdkDragDrop<TreeNode<SortParameter>[]>) {
    const { previousIndex, currentIndex } = event;
    if (previousIndex !== currentIndex) {
      const newNodes = array_move(this.selectedNodes(), previousIndex, currentIndex);
      this.selectedNodes.set(newNodes);
      this.updateValue();
    }
  }

  getPath(key: string): string {
    const parts = key.split('.');
    if (parts.length == 1)
      return "";
    return parts.slice(0, -1).map(x => {
      const translation = this.translate('columns.' + x);
      if (translation.includes('/'))
        return `"${translation}"`;
      return translation;
    }).join(' / ') + ' ';
  }

  updateValue() {
    const sortOnly: SortParameter[] = this.selectedNodes().map(x => ({
      field: x.data.field,
      order: x.data.order,
      nulls: x.data.nulls,
    }));
    this.value.set(sortOnly);
    this.formControl?.setValue(sortOnly);
    this.onChange?.(sortOnly);
  }

  //#region ControlValueAccessor
  onChange: (value: SortParameter[]) => void;
  onTouched: () => void;

  writeValue(obj: SortParameter[]): void {
    if (this.formControl)
      this.formControl?.setValue(obj);
    else {
      this.value.set(obj);
    }
  }
  registerOnChange(fn: (value: SortParameter[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    if (this.formControl) {
      if (isDisabled)
        this.formControl?.disable();
      else
        this.formControl?.enable();
    }
    else {
      this.disabled.set(isDisabled);
    }
  }
  //#endregion
}
